import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  fetchCurrency,
  useCurrenciesQuery,
} from "../../services/CurrencyService";
import { fetchCustomerBill } from "../../services/CustomerBillService";
import {
  addNoteApi,
  exportNote,
  INote,
  MONTH_NAMES_SHORT,
  notePdfApi,
  NoteTypeEnum,
  useNoteQuery,
} from "../../services/NoteService";
import {
  fetchProfile,
  useProfilesDropdown,
  useProfilesQuery,
} from "../../services/ProfilesService";
import { DateRange, IField, Params } from "../../types/table/interfaces.type";
import { fetchProviderBill } from "../../services/ProviderBillService";
import {
  useAccountsQuery,
  useAccountsQueryNA,
} from "../../services/AccountService";
import History from "../../components/History";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Option {
  label: string;
  value: string;
}

const NotePage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<INote & DateRange & { type: string }>
  >({
    search: "",
    type: undefined,
    profileId: "",
  });

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [NoteOptions, setNoteOptions] = useState<Option[]>([]);
  const [filters, setFilters] = useState<Params<INote & { type: string }>>({});
  const [currencyMap, setCurrencyMap] = useState<Record<number, string>>({});
  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [profileMap, setProfileMap] = useState<
    Record<
      string,
      { name: string; accountManagerEmail: string; currencyId: string }
    >
  >({});
  const [filterDeleted, setFilterDeleted] = useState(false);

  const { data: dataProfile } = useProfilesQuery();
  const { data: currencyOption } = useCurrenciesQuery();
  const { data: dataProfiledropdown = [] } = useProfilesDropdown(filterDeleted);

  useEffect(() => {
    if (dataProfile) {
      setProfileOptions(dataProfile.profileOptions);
      setProfileMap(dataProfile.profileMap);
    }
    if (currencyOption) {
      const map = currencyOption.reduce<Record<number, string>>((acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
      }, {});
      setCurrencyMap(map);
    }
  }, [currencyOption, queryClient, dataProfile]);

  const dialogRef = useRef<NoteDialogRef>(null);
  const handleOpenDialog = (mode: "add" | "history", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };
  useEffect(() => {
    const NoteTypeAsOptions = Object.entries(NoteTypeEnum).map(
      ([key, value]) => ({
        label: key,
        value,
      })
    );
    setNoteOptions(NoteTypeAsOptions);
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      type: filters.type,
      profileId: filters.profileId,
    }));
  };

  const autoCompletes = [
    {
      label: "Account Profile",
      value: filters.profileId || "",
      options: dataProfiledropdown,
      isDeleted: filterDeleted,
      clearable: true,
      onToggleDeleted: () => setFilterDeleted((prev) => !prev),
      placeholder: "Select Profile",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfileOption = dataProfiledropdown.find(
          (option) => option.value === value
        );
        setFilters((prev) => ({
          ...prev,
          profileId: String(selectedProfileOption?.value || ""),
        }));
      },
    },
  ];

  const dropdowns = [
    {
      label: "Note Type",
      value: filters.type || "",
      options: NoteOptions,
      placeholder: "Select Note Type",
      onChange: (value: string | number | (string | number)[]) => {
        if (typeof value === "string" && Object.values(NoteTypeEnum)) {
          setFilters((prev) => ({
            ...prev,
            type: value as NoteTypeEnum,
          }));
        } else {
          console.error(`Invalid Note Type selected: ${value}`);
        }
      },
    },
  ];
  const actions = Actions({
    onPdf: {
      handler: async (id: string | number) => {
        await notePdfApi(String(id));
      },
      isDisabled: !hasJwtPermission("GET_NOTE_PDF"),
    },
    // onViewHistory: {
    //   // isDisabled: true,
    //   handler: async (id: string | number) => {
    //     handleOpenDialog("history", String(id));
    //   },
    // },
  });

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_NOTES",
    },
  ];

  const fields = useMemo<IField<INote>[]>(() => [
    { key: "ref", header: "Note Number" },
    {
      key: "type",
      header: "Note Type",
      transform: (row) => {
        const formatted = row.type
          ?.toLowerCase()
          .replace(/_/g, " ")
          .replace(/^\w/, (c) => c.toUpperCase());
        return formatted || "";
      },
    },
    {
      key: "profileId",
      header: "Profile",
      transform: async (t) => {
        if (!t.profileId || t.profileId.length === 0) return "Unknown";

        if (Array.isArray(t.profileId)) {
          const profileNames = t.profileId
            .map((id) => profileMap[id]?.name || "Unknown")
            .join(", ");
          return profileNames || "Unknown";
        }
        const profile = profileMap[t.profileId];
        return profile?.name || "Unknown";
      },
    },
    {
      key: "amount",
      header: "Amount",
      transform: (row) => row.amount.toFixed(3),
    },
    {
      key: "id",
      header: "Currency",
      transform: async (row) => {
        const profileId = Array.isArray(row.profileId)
          ? row.profileId[0]
          : row.profileId;
        if (!profileId) return "-";
        try {
          const profile = await queryClient.fetchQuery({
            queryKey: ["profile", profileId],
            queryFn: () => fetchProfile(String(profileId)),
          });
          if (!profile?.currencyId) return "-";
          const currency = await queryClient.fetchQuery({
            queryKey: ["currency", profile.currencyId],
            queryFn: () => fetchCurrency(Number(profile.currencyId)),
          });
          return currency?.name ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      key: "month",
      header: "Note Date",
      transform: async (row: INote) => {
        const monthIndex = row.month - 1;
        if (monthIndex < 0 || monthIndex > 11) {
          return "";
        }

        const monthName = MONTH_NAMES_SHORT[monthIndex];
        return `${monthName}, ${row.year}`;
      },
    },
    {
      key: "invoiceId",
      header: "Invoice Number",
      transform: async (row) => {
        if (!row.invoiceId || !row.type) return "-";
        try {
          if (row.type === NoteTypeEnum.CREDIT) {
            const result = await queryClient.fetchQuery({
              queryKey: ["customerBillList", row.invoiceId],
              queryFn: () => fetchCustomerBill({ id: Number(row.invoiceId) }),
            });
            return result?.content?.[0]?.ref ?? "-";
          } else if (row.type === NoteTypeEnum.DEBIT) {
            const result = await queryClient.fetchQuery({
              queryKey: ["providerBillList", row.invoiceId],
              queryFn: () => fetchProviderBill({ id: Number(row.invoiceId) }),
            });
            return result?.content?.[0]?.ref ?? "-";
          }
          return "-";
        } catch {
          return "-";
        }
      },
    },
    { key: "createdDate", header: "Created Date" },
    { key: "createdBy", header: "User" },
  ], [queryClient, profileMap]);

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          dropdowns={dropdowns}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Note List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_NOTE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportNote(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_NOTES_CSV",
            },
          ]}
        />

        <CustomTable<INote>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useNoteQuery}
          actions={actions}
          sortableFields={[
            "ref",
            "amount",
            "month",
            "createdDate",
            "createdBy",
          ]}
        />
      </Box>
      <NoteDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        profileOptions={profileOptions}
        NoteOptions={NoteOptions}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

interface NoteDialogRef {
  openDialog: (mode: "add" | "history", id?: string) => void;
}

interface NoteDialogProps {
  onRefresh: () => void;
  profileOptions: Option[];
  appliedFilters: Params<INote & DateRange & { type: string }>;
  NoteOptions: Option[];
}

const NoteDialog = forwardRef<NoteDialogRef, NoteDialogProps>(
  ({ onRefresh, profileOptions, NoteOptions, appliedFilters }, ref) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "history">("add");
    const [dialogData, setDialogData] = useState<
      Partial<
        INote & { currencyName: string; type: string; accountType: string }
      >
    >({
      createdDate: new Date().toISOString(),
      id: "",
      amount: 0,
      profileId: "",
      year: new Date().getFullYear(),
      invoiceId: "",
      comment: "",
      accountType: "CLIENT",
    });

    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [invoiceSearch, setInvoiceSearch] = useState("");
    const [selectedInvoiceOption, setSelectedInvoiceOption] =
      useState<Option | null>(null);
    const invoiceSearchTimeoutRef = useRef<number | undefined>(undefined);

    const isCreditNote = dialogData.type === NoteTypeEnum.CREDIT;
    const isDebitNote = dialogData.type === NoteTypeEnum.DEBIT;
    const { data: invoiceBills, isError: isInvoiceError } = useQuery({
      queryKey: ["noteInvoiceOptions", dialogData.type, invoiceSearch],
      queryFn: async () => {
        const params = invoiceSearch ? { search: invoiceSearch } : {};
        const page = isCreditNote
          ? await fetchCustomerBill(params)
          : await fetchProviderBill(params);
        return page.content.map((bill) => ({ id: bill.id, ref: bill.ref }));
      },
      enabled: isCreditNote || isDebitNote,
    });

    useEffect(() => {
      if (isInvoiceError) {
        ToastNotification.error(
          `Failed to fetch ${isCreditNote ? "CUSTOMER" : "PROVIDER"} bill options.`
        );
      }
    }, [isInvoiceError, isCreditNote]);

    const invoiceOptions = useMemo<Option[]>(() => {
      if (!isCreditNote && !isDebitNote) return [];
      // Reuse the selected option's object so MUI doesn't treat new search results as a value change and reset the typed input
      const options = (invoiceBills ?? []).map((bill) =>
        selectedInvoiceOption && String(bill.id) === selectedInvoiceOption.value
          ? selectedInvoiceOption
          : { label: bill.ref, value: String(bill.id) }
      );
      if (selectedInvoiceOption && !options.includes(selectedInvoiceOption)) {
        return [selectedInvoiceOption, ...options];
      }
      return options;
    }, [isCreditNote, isDebitNote, invoiceBills, selectedInvoiceOption]);

    const resetInvoiceSearch = () => {
      window.clearTimeout(invoiceSearchTimeoutRef.current);
      setInvoiceSearch("");
    };

    const handleInvoiceInputChange = (
      _event: React.SyntheticEvent,
      value: string,
      reason?: string
    ) => {
      if (reason !== "input") {
        resetInvoiceSearch();
        return;
      }
      window.clearTimeout(invoiceSearchTimeoutRef.current);
      invoiceSearchTimeoutRef.current = window.setTimeout(() => {
        setInvoiceSearch(value.trim());
      }, 200);
    };

    useEffect(() => {
      return () => window.clearTimeout(invoiceSearchTimeoutRef.current);
    }, []);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setDialogOpen(true);
        setDialogData((prev) => ({
          ...prev,
          year: prev.year || new Date().getFullYear(),
          invoiceId: prev.invoiceId || undefined,
        }));
      },
    }));

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useEffect(() => {
      const isAnyFieldEmpty =
        !dialogData.profileId ||
        !dialogData.type ||
        !dialogData.month ||
        !dialogData.year ||
        !dialogData.comment;

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const { data: AccountOptions } = useAccountsQueryNA({
      profileId: dialogData.profileId,
      type: dialogData.type === "CREDIT_NOTE" ? "CLIENT" : "VENDOR",
    });
    const accountOptionsList: Option[] = (AccountOptions?.content ?? []).map(
      (account: { name: string; id: string }) => ({
        label: account.name,
        value: account.id,
      })
    );

    useEffect(() => {
      if (dialogMode === "add") {
        openDialog("add");
      }
    }, [dialogMode]);

    const openDialog = async (mode: "add", id?: string) => {
      setDialogMode(mode);

      try {
        setDialogData({});
        setSelectedInvoiceOption(null);
        resetInvoiceSearch();
      } catch (error: any) {
        console.error(`Error fetching Note data for ${mode} mode:`, error);
      }
    };

    const closeDialog = () => setDialogOpen(false);

    const addNote = async (
      data: Partial<INote & { accountType: string; currencyName: string }>
    ) => {
      try {
        const { accountType, currencyName, ...apiData } = data;

        await addNoteApi({
          ...apiData,
          connectionProfileIds: data.connectionProfileIds,
        });

        onRefresh();
        closeDialog();
        setDialogData({});

        ToastNotification.success("Note added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const fieldHistory = useMemo<IField<INote>[]>(() => [
      { key: "ref", header: "Note Number" },
      {
        key: "type",
        header: "Note Type",
        transform: (row) => {
          const formatted = row.type
            ?.toLowerCase()
            .replace(/_/g, " ")
            .replace(/^\w/, (c) => c.toUpperCase());
          return formatted || "";
        },
      },
      // {
      //   key: "profileId",
      //   header: "Profile",
      //   transform: async (t) => {
      //     if (!t.profileId || t.profileId.length === 0) return "Unknown";

      //     if (Array.isArray(t.profileId)) {
      //       const profileNames = t.profileId
      //         .map((id) => profileMap[id]?.name || "Unknown")
      //         .join(", ");
      //       return profileNames || "Unknown";
      //     }
      //     const profile = profileMap[t.profileId];
      //     return profile?.name || "Unknown";
      //   },
      // },
      {
        key: "amount",
        header: "Amount",
        transform: (row) => row.amount.toFixed(3),
      },
      {
        key: "id",
        header: "Currency",
        transform: async (row) => {
          const profileId = Array.isArray(row.profileId)
            ? row.profileId[0]
            : row.profileId;
          if (!profileId) return "-";
          try {
            const profile = await queryClient.fetchQuery({
              queryKey: ["profile", profileId],
              queryFn: () => fetchProfile(String(profileId)),
            });
            if (!profile?.currencyId) return "-";
            const currency = await queryClient.fetchQuery({
              queryKey: ["currency", profile.currencyId],
              queryFn: () => fetchCurrency(Number(profile.currencyId)),
            });
            return currency?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "month",
        header: "Note Date",
        transform: async (row: INote) => {
          const monthIndex = row.month - 1;
          if (monthIndex < 0 || monthIndex > 11) {
            return "";
          }

          const monthName = MONTH_NAMES_SHORT[monthIndex];
          return `${monthName}, ${row.year}`;
        },
      },
      { key: "ref", header: "invoice Number" },
      { key: "createdDate", header: "Creadted Date" },
      { key: "updatedBy", header: "Updated By" },
      { key: "createdBy", header: "User" },
    ], [queryClient]);

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        mode={dialogMode}
        dialogSx={{ minWidth: 800 }}
        title={dialogMode === "history" ? "History" : "Add New Note"}
        fieldsPerRow={2}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History<INote>
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={useNoteQuery}
                    />
                  ),
                },
              ]
            : [
                {
                  type: "select",
                  label: "Note Type",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  options: NoteOptions,
                  value: dialogData.type || "",
                  onChange: async (
                    value: string | number | (string | number)[]
                  ) => {
                    if (
                      typeof value === "string" &&
                      Object.values(NoteTypeEnum)
                    ) {
                      setDialogData((prev) => ({
                        ...prev,
                        type: value as NoteTypeEnum,
                        invoiceId: "",
                      }));
                      setSelectedInvoiceOption(null);
                      resetInvoiceSearch();

                      if (value === NoteTypeEnum.CREDIT) {
                        setDialogData((prev) => ({
                          ...prev,
                          accountType: "CLIENT",
                        }));
                      } else if (value === NoteTypeEnum.DEBIT) {
                        setDialogData((prev) => ({
                          ...prev,
                          accountType: "VENDOR",
                        }));
                      }

                      validateField("NoteType", String(value));
                    } else {
                      console.error(`Invalid Note Type selected: ${value}`);
                    }
                  },
                  placeholder: "Select Note Type",
                  error: !!errors.type,
                  helperText: errors.type ? "Note Type is required" : "",
                },
                {
                  type: "text",
                  typeinput: "number",
                  label: "Amount",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value:
                    dialogData.amount !== undefined &&
                    dialogData.amount !== null
                      ? dialogData.amount
                      : "",
                  onChange: (value) => {
                    const numericValue =
                      value === "" ? undefined : Number(value);
                    setDialogData((prev) => ({
                      ...prev,
                      amount: numericValue,
                    }));
                  },
                  placeholder: "Amount",
                  error: !!errors.amount,
                  helperText: errors.amount ? "Amount is required" : "",
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Account Profile"
                      value={dialogData.profileId || ""}
                      options={profileOptions}
                      onChange={async (
                        value: string | number | (string | number)[]
                      ) => {
                        const selectedProfileId = String(value);
                        setDialogData((prev) => ({
                          ...prev,
                          profileId: selectedProfileId,
                        }));
                        validateField("selectedProfileId", selectedProfileId);

                        // Only fetch profile and currency if a value is selected
                        if (selectedProfileId && selectedProfileId !== "") {
                          try {
                            const profile = await fetchProfile(
                              selectedProfileId
                            );
                            const currency = await fetchCurrency(
                              profile.currencyId
                            );
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: currency.name,
                            }));
                          } catch (error: any) {
                            console.error(
                              "Error fetching currency for selected provider:",
                              error
                            );
                            ToastNotification.error(error.message);
                            setDialogData((prev) => ({
                              ...prev,
                              currencyName: "",
                            }));
                          }
                        } else {
                          // Clear currency name when profile is cleared
                          setDialogData((prev) => ({
                            ...prev,
                            currencyName: "",
                          }));
                        }
                      }}
                      clearable={true}
                      disablePortal={false}
                      error={!!errors.accountProfile}
                      placeholder="Select Account Profile"
                      highlightLabelStar={true}
                      helperText={
                        errors.accountProfile
                          ? "Account Profile is required"
                          : ""
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "text",
                  label: "Currency",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: false,
                  value: dialogData.currencyName || "",
                  disabled: true,
                  onChange: () => {},
                  placeholder: "Existing Currency",
                },

                {
                  type: "select",
                  label: "Account Type",
                  placeholder: "Select Account Type",
                  options: [
                    { label: "Customer", value: "CLIENT" },
                    { label: "Provider", value: "VENDOR" },
                  ],
                  renderPlaceholder: true,
                  value: dialogData.accountType || "",
                  bgColor: COLORS.WhiteBackground,
                  disabled: true,
                  highlightLabelStar: true,
                  onChange: async (
                    value: string | number | (string | number)[]
                  ) => {
                    setDialogData((prev) => ({
                      ...prev,
                      accountType: String(value),
                    }));
                  },
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Accounts"
                      multiple={true}
                      selectAll={true}
                      hideChips={true}
                      value={
                        Array.isArray(dialogData.connectionProfileIds)
                          ? dialogData.connectionProfileIds.map(String)
                          : []
                      }
                      options={accountOptionsList}
                      onChange={(
                        value: string | number | (string | number)[]
                      ) => {
                        const numberArray = Array.isArray(value)
                          ? value.map((v) => Number(v))
                          : value
                          ? [Number(value)]
                          : [];

                        setDialogData((prev) => ({
                          ...prev,
                          connectionProfileIds: numberArray,
                        }));
                      }}
                      clearable={true}
                      disablePortal={false}
                      error={!!errors.connectionProfileIds}
                      placeholder="Select Accounts"
                      highlightLabelStar={true}
                      helperText={
                        errors.connectionProfileIds
                          ? "Customer is required"
                          : ""
                      }
                      disabled={!dialogData.type}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Invoice"
                      value={dialogData.invoiceId || ""}
                      options={invoiceOptions}
                      onChange={(
                        value: string | number | (string | number)[]
                      ) => {
                        setDialogData((prev) => ({
                          ...prev,
                          invoiceId: String(value),
                        }));
                        setSelectedInvoiceOption(
                          invoiceOptions.find(
                            (option) => option.value === String(value)
                          ) ?? null
                        );
                      }}
                      onInputChange={handleInvoiceInputChange}
                      clearable={true}
                      placeholder="Select Invoice"
                      disablePortal={false}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "select",
                  label: "Month",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.month || "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      month: Number(value),
                    }));
                    validateField("month", value as string);
                  },
                  options: [
                    { label: "January", value: 1 },
                    { label: "February", value: 2 },
                    { label: "March", value: 3 },
                    { label: "April", value: 4 },
                    { label: "May", value: 5 },
                    { label: "June", value: 6 },
                    { label: "July", value: 7 },
                    { label: "August", value: 8 },
                    { label: "September", value: 9 },
                    { label: "October", value: 10 },
                    { label: "November", value: 11 },
                    { label: "December", value: 12 },
                  ],
                  error: !!errors.month,
                  helperText: errors.month ? "Month is required" : "",
                },
                {
                  type: "select",
                  label: "Year",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.year || new Date().getFullYear(),
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      year: Number(value),
                    }));
                    validateField("year", value as string);
                  },
                  options: Array.from(
                    { length: new Date().getFullYear() - 2000 + 1 },
                    (_, index) => ({
                      label: (2000 + index).toString(),
                      value: (2000 + index).toString(),
                    })
                  ),
                  error: !!errors.year,
                  helperText: errors.year ? "Year is required" : "",
                },
                {
                  type: "text",
                  label: "Comment",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.comment || "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      comment: String(value),
                    }));
                  },
                  placeholder: "Enter a comment",
                  error: !!errors.comment,
                  helperText: errors.comment ? "Comment is required" : "",
                },
              ]
        }
        buttons={[
          {
            label: "Save",
            variant: "contained" as "contained",
            bgColor: COLORS.GOLD,
            borderRadius: "12px",
            onClick: async () => {
              await addNote(dialogData);
            },
            disabled: isSaveDisabled || !hasJwtPermission("POST_NOTE"),
          },
        ]}
      />
    );
  }
);

export default NotePage;
